import {
  SchemaFormGroup,
  SchemaFormArray,
  SchemaFormControl,
} from '@apexdesigner/schema-forms';
import type { SchemaType } from '@apexdesigner/schema-forms';
import { z } from 'zod';
import type { DestroyRef } from '@angular/core';
import { Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, tap } from 'rxjs';
import createDebug from 'debug';
import { createCounter } from '@apexdesigner/pending-work';
import {
  getRelationshipMetadata,
  inferForeignKey,
} from '@apexdesigner/schema-tools';

const Debug = createDebug('PersistedForm');

/** Strip client-only options (disabled, required) from a filter before sending to the API */
function stripClientOptions(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripClientOptions);
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'disabled' || key === 'required') continue;
    result[key] =
      key === 'include' && typeof value === 'object'
        ? stripClientOptions(value)
        : value;
  }
  return result;
}

/** Apply client-only include options (disabled, required) to a group's controls */
function applyClientOptions(
  group: { controls: Record<string, any> },
  options: any,
  label: string,
): void {
  if (!options || typeof options !== 'object') return;
  if (Array.isArray(options.disabled)) {
    for (const fieldName of options.disabled) {
      const control = group.controls[fieldName];
      if (control) {
        Debug('disabling nested field %s.%s', label, fieldName);
        control.disable();
      }
    }
  }
  if (Array.isArray(options.required)) {
    for (const fieldName of options.required) {
      const control = group.controls[fieldName];
      if (control) {
        Debug('requiring nested field %s.%s', label, fieldName);
        control.addValidators(Validators.required);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }
}

export interface EntityClass {
  findById(id: any, filter?: any): Promise<any>;
  find(filter?: any): Promise<any[]>;
  create(data: any): Promise<any>;
  updateById(id: any, data: any): Promise<any>;
}

export interface PersistedFormGroupOptions {
  filter?: Record<string, any>;
  required?: string[];
  disabled?: string[];
}

export class PersistedFormGroup extends SchemaFormGroup {
  reading = false;
  saving = false;
  afterRead: (() => void) | null = null;
  afterSave: (() => void) | null = null;
  private _filter: Record<string, any> = {};
  private _entityClass: EntityClass;
  private _idProperty: string;
  private _savePromise: Promise<any> | null = null;
  private _autoSaveDestroyRef: DestroyRef | null = null;
  private _autoSaveDebounceMs = 300;
  _parentForeignKey: string | null = null;

  constructor(
    schema: SchemaType,
    entityClass: EntityClass,
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
    idProperty = 'id',
  ) {
    super(schema);
    this._entityClass = entityClass;
    this._idProperty = idProperty;
    if (options?.filter) this._filter = options.filter;
    if (options?.required) {
      for (const name of options.required) {
        const control = this.controls[name];
        if (control) control.addValidators(Validators.required);
      }
    }
    if (options?.disabled) {
      for (const name of options.disabled) {
        const control = this.controls[name];
        if (control) control.disable();
      }
    }
    if (data) {
      this._populate(data);
    }
    this.updateOriginalValue();
  }

  /** Name of the id property on the bound business object (defaults to "id"). */
  get idProperty(): string {
    return this._idProperty;
  }

  /** Returns the id value of the current record, reading even disabled controls. */
  getId(): any {
    return (this.getRawValue() as Record<string, any>)?.[this._idProperty];
  }

  get readFilter(): Record<string, any> {
    return this._filter;
  }

  set readFilter(value: Record<string, any>) {
    this._filter = value;
  }

  async read(filter?: Record<string, any>): Promise<void> {
    const debug = Debug.extend('read');
    // Wait for in-progress save before reading (design decision #5)
    if (this._savePromise) await this._savePromise;

    const mergedFilter = { ...this._filter, ...filter };
    if (filter) this._filter = mergedFilter;
    const apiFilter = stripClientOptions(mergedFilter);
    this.reading = true;
    try {
      const id = mergedFilter.where?.[this._idProperty];
      let data: Record<string, any>;
      if (id !== undefined && id !== null) {
        data = await this._entityClass.findById(id, apiFilter);
      } else {
        const results = await this._entityClass.find(apiFilter);
        data = results[0];
        if (!data) throw new Error('No record found matching filter');
      }
      debug('data', data);
      this._populate(data);
      debug('value after read', this.value);
      this.updateOriginalValue();
    } finally {
      this.reading = false;
    }
    if (this.afterRead) this.afterRead();
  }

  protected createControl(_name: string): any {
    return undefined;
  }

  _populate(data: Record<string, any>): void {
    const debug = Debug.extend('_populate');
    // This form group's own entity name (from the schema's x-name), used to infer
    // convention foreign keys on included has-many/has-one children. Convention-based
    // hasMany()/hasOne() omit the FK from the schema meta, so relMeta.foreignKey is
    // undefined; inferForeignKey rebuilds the same {parentEntity}Id the generator dropped.
    const parentEntityName = (this.schema as any)?.meta?.()?.['x-name'] as
      string | undefined;
    // Lazily create controls for included relationships (including null has-one)
    for (const key of Object.keys(data)) {
      if (typeof data[key] === 'object' && !this.controls[key]) {
        const control = this.createControl(key);
        if (control) {
          debug('lazy-creating control %s', key);
          this.setControl(key, control);
          if (
            this._autoSaveDestroyRef &&
            control instanceof PersistedFormGroup
          ) {
            const fieldSchema = this.schema?.shape?.[key];
            if (fieldSchema) {
              const relMeta = getRelationshipMetadata(fieldSchema as any);
              if (
                relMeta?.relationshipType === 'hasOne' &&
                data[this._idProperty]
              ) {
                const fk =
                  relMeta.foreignKey ??
                  (parentEntityName
                    ? inferForeignKey(key, 'hasOne', parentEntityName)
                    : undefined);
                if (fk) {
                  debug(
                    'setting FK %s=%o on child %s',
                    fk,
                    data[this._idProperty],
                    key,
                  );
                  control.patchValue({ [fk]: data[this._idProperty] });
                  control._parentForeignKey = fk;
                }
              }
            }
            control.autoSave(
              this._autoSaveDestroyRef,
              this._autoSaveDebounceMs,
            );
            control.updateOriginalValue();
          }
        }
      }
    }

    // Reset scalar and embedded controls not present in data (API omits null fields)
    for (const [key, control] of Object.entries(this.controls)) {
      if (control instanceof SchemaFormControl && !(key in data)) {
        control.reset(null, { emitEvent: false });
      } else if (this._isEmbedded(control) && !(key in data)) {
        if (control instanceof SchemaFormArray)
          control.clear({ emitEvent: false });
        else control.reset(undefined, { emitEvent: false });
      }
    }

    this.patchValue(data);

    // Populate nested relationship controls (recursively)
    for (const [name, control] of Object.entries(this.controls)) {
      if (control instanceof PersistedFormArray && Array.isArray(data[name])) {
        debug('populating array', name, 'with', data[name].length, 'items');
        // Set parent context so add() can inject the foreign key
        const fieldSchema = this.schema?.shape?.[name];
        if (fieldSchema) {
          const relMeta = getRelationshipMetadata(fieldSchema as any);
          if (
            relMeta?.relationshipType === 'hasMany' &&
            data[this._idProperty]
          ) {
            const fk =
              relMeta.foreignKey ??
              (parentEntityName
                ? inferForeignKey(name, 'hasMany', parentEntityName)
                : undefined);
            if (fk) {
              control._parentForeignKey = fk;
              control._parentId = data[this._idProperty];
              debug(
                'set parent FK %s=%o on array %s',
                fk,
                data[this._idProperty],
                name,
              );
            }
          }
        }
        // Propagate the include sub-filter to the child array's readFilter
        const includeFilter = this._filter?.include?.[name];
        if (includeFilter && typeof includeFilter === 'object') {
          control.readFilter = { ...control.readFilter, ...includeFilter };
          debug('set readFilter on array %s: %j', name, control.readFilter);
        }
        // Propagate autoSave to the array before adding items
        if (this._autoSaveDestroyRef) {
          control.autoSave(this._autoSaveDestroyRef, this._autoSaveDebounceMs);
        }
        control.clear();
        for (const item of data[name]) {
          control.addItem(item);
        }
      }
      if (
        control instanceof PersistedFormGroup &&
        data[name] &&
        typeof data[name] === 'object' &&
        !Array.isArray(data[name])
      ) {
        debug('populating nested form group', name);
        const nestedInclude = this._filter?.include?.[name];
        // Hand the sub-include down before recursing so client options nested
        // deeper in the include tree apply at every level
        if (
          nestedInclude?.include &&
          typeof nestedInclude.include === 'object'
        ) {
          control.readFilter = {
            ...control.readFilter,
            include: nestedInclude.include,
          };
        }
        control._populate(data[name]);
        control.updateOriginalValue();
        // Apply disabled/required fields from nested include options
        applyClientOptions(control, nestedInclude, name);
      }
    }
  }

  override async save(): Promise<any> {
    const debug = Debug.extend('save');
    debug('save called');
    this.saving = true;
    this._savePromise = this._doSave();
    let result: any;
    try {
      result = await this._savePromise;
    } finally {
      this._savePromise = null;
      this.saving = false;
    }
    // Notify only when a request was actually sent (null means nothing had
    // changed), and only once the in-flight state is cleared so a read() or
    // another save() inside the callback is not deferred behind this one
    if (result !== null && this.afterSave) this.afterSave();
    const parent = this.parent;
    if (
      result !== null &&
      parent instanceof PersistedFormArray &&
      parent.afterSave
    )
      parent.afterSave();
    return result;
  }

  /**
   * Values that persist inline with this record: scalar controls and embedded
   * sub-objects. Relationship controls are left out — they persist themselves.
   */
  private _inlineOnly(data: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key of Object.keys(data)) {
      const control = this.controls[key];
      if (control instanceof SchemaFormControl || this._isEmbedded(control)) {
        result[key] = data[key];
      }
    }
    return result;
  }

  /**
   * Whether a control is an embedded sub-object — a plain SchemaFormGroup or
   * SchemaFormArray built from a nested schema shape and stored inside this
   * record — as opposed to a persisted relationship control.
   */
  private _isEmbedded(control: any): boolean {
    return (
      (control instanceof SchemaFormGroup &&
        !(control instanceof PersistedFormGroup)) ||
      (control instanceof SchemaFormArray &&
        !(control instanceof PersistedFormArray))
    );
  }

  private async _doSave(): Promise<any> {
    const debug = Debug.extend('_doSave');
    const id = this.getId();
    if (id) {
      const changes = this.getChanges();
      debug('changes %j', changes);
      if (!changes) return null;
      const scalar = this._inlineOnly(changes);
      debug('scalar changes %j', scalar);
      if (Object.keys(scalar).length === 0) return null;
      const sentTimeValues = { ...this.originalValue, ...scalar };
      const result = await this._entityClass.updateById(id, scalar);
      // Patch back only server-computed fields that actually changed (e.g. lastModified),
      // skipping fields the user edited while the request was in flight — the
      // user's value stays on screen and the next autosave pass persists it
      const serverUpdates: Record<string, any> = {};
      for (const key of Object.keys(result)) {
        if (key in scalar) continue;
        if (this._editedInFlight(key, sentTimeValues)) continue;
        if (result[key] !== this.value?.[key]) {
          serverUpdates[key] = result[key];
        }
      }
      if (Object.keys(serverUpdates).length > 0) this.patchValue(serverUpdates);
      this._updateBaselineAfterSave(scalar, serverUpdates);
      return result;
    } else {
      const payload = this._inlineOnly(this.getPayload());
      debug('create payload %j', payload);
      const result = await this._entityClass.create(payload);
      // Patch in server-assigned fields (id, timestamps, defaults), skipping
      // fields the user edited while the request was in flight
      const serverUpdates: Record<string, any> = {};
      for (const key of Object.keys(result)) {
        if (this._editedInFlight(key, payload)) continue;
        if (result[key] !== this.value?.[key]) {
          serverUpdates[key] = result[key];
        }
      }
      if (Object.keys(serverUpdates).length > 0) this.patchValue(serverUpdates);
      this._updateBaselineAfterSave(payload, serverUpdates);
      return result;
    }
  }

  /**
   * Whether a scalar field's current value differs from its value at the time
   * the save request was sent — i.e. the user edited it while the request was
   * in flight. Such edits must not be clobbered by the response or absorbed
   * into the baseline; the next autosave pass persists them.
   */
  private _editedInFlight(
    key: string,
    sentTimeValues: Record<string, any>,
  ): boolean {
    const control = this.controls[key];
    const sentTimeValue = Object.prototype.hasOwnProperty.call(
      sentTimeValues,
      key,
    )
      ? sentTimeValues[key]
      : null;
    if (this._isEmbedded(control)) {
      // Payloads on both sides come from the same form structure, so key order matches
      const embedded = control as SchemaFormGroup | SchemaFormArray;
      return (
        JSON.stringify(embedded.getPayload() ?? null) !==
        JSON.stringify(sentTimeValue ?? null)
      );
    }
    if (!(control instanceof SchemaFormControl)) return false;
    return (control.value ?? null) !== (sentTimeValue ?? null);
  }

  /**
   * Rebuilds the change-tracking baseline after a save from what was actually
   * persisted — never from live form state. A baseline derived from getPayload()
   * absorbs edits made while the request was in flight, so getChanges() returns
   * null and those edits silently never save. Scalar fields and embedded
   * sub-objects come from the server response, the sent payload, or the
   * previous baseline; relationship controls keep the live snapshot because
   * they track their own baselines.
   */
  private _updateBaselineAfterSave(
    sent: Record<string, any>,
    serverValues: Record<string, any>,
  ): void {
    const baseline = this.getPayload();
    const previous = this.originalValue ?? {};
    for (const [key, control] of Object.entries(this.controls)) {
      if (!(control instanceof SchemaFormControl) && !this._isEmbedded(control))
        continue;
      if (key in serverValues) {
        baseline[key] = serverValues[key];
      } else if (key in sent) {
        baseline[key] = sent[key];
      } else if (Object.prototype.hasOwnProperty.call(previous, key)) {
        baseline[key] = previous[key];
      } else {
        delete baseline[key];
      }
    }
    this.updateOriginalValue(baseline);
  }

  autoSave(destroyRef: DestroyRef, debounceMs = 300): void {
    const debug = Debug.extend('autoSave');
    debug('autoSave registered');
    this._autoSaveDestroyRef = destroyRef;
    this._autoSaveDebounceMs = debounceMs;

    // Report pending-save state (debounce armed through save response landed)
    // in the shared pending-work registry, so app shells and the e2e settled()
    // fixture know the UI has not settled. The counter arms BEFORE the
    // debounce: right after an edit there is no request in flight, and that
    // quiet gap is exactly what network-idle heuristics miss. Each debounced
    // emission consumes the armed unit and holds it until its save lands, so
    // an edit during an in-flight save keeps the aggregate busy until the
    // last save settles.
    const pendingSaves = createCounter('autosave');
    let debouncePending = false;
    this.valueChanges
      .pipe(
        tap(() => {
          if (!debouncePending) {
            debouncePending = true;
            pendingSaves.begin();
          }
        }),
        debounceTime(debounceMs),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe(() => {
        debouncePending = false;
        const hasId = !!this.getId();
        const canAutoCreate = !hasId && !!this._parentForeignKey;
        const shouldSave =
          this.hasUnsavedChanges && !this.reading && (hasId || canAutoCreate);
        debug(
          'debounced: hasUnsavedChanges=%o saving=%o reading=%o id=%o canAutoCreate=%o → %o',
          this.hasUnsavedChanges,
          this.saving,
          this.reading,
          this.getId(),
          canAutoCreate,
          shouldSave,
        );
        if (!shouldSave) {
          pendingSaves.end();
          return;
        }
        if (this.saving && this._savePromise) {
          // A save is already in flight; re-run the check once it settles so
          // edits made during the request are saved rather than stranded
          // until the next keystroke
          debug('autoSave deferring until in-flight save settles');
          this._savePromise
            .catch(() => undefined)
            .then(() => {
              if (this.hasUnsavedChanges && !this.saving && !this.reading) {
                debug('autoSave saving changes made during in-flight save');
                return this.save().catch((error) =>
                  debug('autoSave save error %o', error),
                );
              }
              return undefined;
            })
            .finally(() => pendingSaves.end());
          return;
        }
        debug('autoSave triggering save');
        this.save()
          .catch((error) => debug('autoSave save error %o', error))
          .finally(() => pendingSaves.end());
      });

    // Flush pending changes on destroy. The counter is unregistered rather
    // than ended: the destroyed form can no longer settle its own units, and
    // the flush save's HTTP request is counted by the interceptor.
    destroyRef.onDestroy(() => {
      pendingSaves.unregister();
      const hasId = !!this.getId();
      const canAutoCreate = !hasId && !!this._parentForeignKey;
      if (this.hasUnsavedChanges && !this.saving && (hasId || canAutoCreate)) {
        debug('onDestroy flush: saving pending changes');
        this.save();
      }
    });
  }
}

export interface EntityArrayClass {
  find(filter?: any): Promise<any[]>;
  create(data: any): Promise<any>;
  deleteById(id: any): Promise<any>;
}

/**
 * Custom read for an array property (@property({ readBehavior: Bo.someBehavior })).
 * Called with the merged filter in place of entityClass.find().
 */
export type ReadBehavior = (filter?: Record<string, any>) => Promise<any[]>;

export interface PersistedFormArrayOptions {
  filter?: Record<string, any>;
  readBehavior?: ReadBehavior;
}

export class PersistedFormArray extends SchemaFormArray {
  reading = false;
  afterRead: (() => void) | null = null;
  /** Invoked after any row's save() completes — rows save individually via autoSave */
  afterSave: (() => void) | null = null;
  readonly entityName: string = '';
  _parentForeignKey: string | null = null;
  _parentId: any = null;
  _autoSaveDestroyRef: DestroyRef | null = null;
  _autoSaveDebounceMs = 300;

  private _filter: Record<string, any> = {};
  private _entityClass: EntityArrayClass;
  private _idProperty: string;
  private _readBehavior?: ReadBehavior;

  constructor(
    itemSchema: SchemaType,
    entityClass: EntityArrayClass,
    options?: PersistedFormArrayOptions,
    idProperty = 'id',
  ) {
    super(z.array(itemSchema) as any);
    this._entityClass = entityClass;
    this._idProperty = idProperty;
    if (options?.filter) this._filter = options.filter;
    this._readBehavior = options?.readBehavior;
  }

  protected createItemGroup(): any {
    return undefined;
  }

  override addItem(data?: any): void {
    const group = this.createItemGroup();
    if (group) {
      if (data && group instanceof PersistedFormGroup) {
        // Hand the sub-include down before populating so client options nested
        // deeper in the include tree apply inside each row
        if (this._filter.include && typeof this._filter.include === 'object') {
          group.readFilter = {
            ...group.readFilter,
            include: this._filter.include,
          };
        }
        group._populate(data);
      } else if (data) {
        group.patchValue(data);
      }
      if (this._autoSaveDestroyRef && group instanceof PersistedFormGroup) {
        group.autoSave(this._autoSaveDestroyRef, this._autoSaveDebounceMs);
        group.updateOriginalValue();
      }
      // Apply this array's own client options (disabled, required) to the row —
      // after the baseline capture, matching the nested to-one ordering
      if (group instanceof PersistedFormGroup) {
        applyClientOptions(group, this._filter, this.entityName || 'item');
      }
      this.push(group);
    } else {
      super.addItem(data);
    }
  }

  autoSave(destroyRef: DestroyRef, debounceMs = 300): void {
    this._autoSaveDestroyRef = destroyRef;
    this._autoSaveDebounceMs = debounceMs;
    for (const control of this.controls) {
      if (control instanceof PersistedFormGroup) {
        control.autoSave(destroyRef, debounceMs);
        control.updateOriginalValue();
      }
    }
  }

  get readFilter(): Record<string, any> {
    return this._filter;
  }

  set readFilter(value: Record<string, any>) {
    this._filter = value;
  }

  async read(filter?: Record<string, any>): Promise<void> {
    const mergedFilter = this._buildFilterWithParentFK({
      ...this._filter,
      ...filter,
    });
    if (filter) this._filter = { ...this._filter, ...filter };
    const apiFilter = stripClientOptions(mergedFilter);
    this.reading = true;
    try {
      const items = this._readBehavior
        ? await this._readBehavior(apiFilter)
        : await this._entityClass.find(apiFilter);
      this.clear();
      for (const item of items) {
        this.addItem(item);
      }
    } finally {
      this.reading = false;
    }
    if (this.afterRead) this.afterRead();
  }

  private _buildFilterWithParentFK(
    filter: Record<string, any>,
  ): Record<string, any> {
    if (this._parentForeignKey && this._parentId != null) {
      return {
        ...filter,
        where: { ...filter.where, [this._parentForeignKey]: this._parentId },
      };
    }
    return filter;
  }

  async add(data?: Record<string, any>): Promise<any> {
    const payload = { ...data };
    if (this._parentForeignKey && this._parentId != null) {
      payload[this._parentForeignKey] = this._parentId;
    }
    const result = await this._entityClass.create(payload);
    this.addItem(result);
    return result;
  }

  /**
   * Value of a control including its disabled controls; a plain object passes
   * through unchanged. FormGroup.value drops disabled controls, so a read-only
   * id field would otherwise make a row look like it has no id.
   */
  private _rawValueOf(item: any): Record<string, any> {
    return (
      (typeof item?.getRawValue === 'function' ? item.getRawValue() : item) ??
      {}
    );
  }

  /** Id of a group, read so a disabled id control still counts. */
  private _readId(group: any): any {
    return this._rawValueOf(group)[this._idProperty];
  }

  async remove(indexOrItem: number | Record<string, any>): Promise<void> {
    let index: number;
    if (typeof indexOrItem === 'number') {
      index = indexOrItem;
    } else {
      const groups = this.getGroups();
      const itemData = this._rawValueOf(indexOrItem);
      index = groups.findIndex(
        (g) => this._readId(g) === itemData[this._idProperty],
      );
      if (index === -1) return;
    }

    const group = this.getGroups()[index];
    const id = this._readId(group);
    if (id) {
      await this._entityClass.deleteById(id);
    }
    this.removeItem(index);
  }
}

export interface PersistedArrayOptions {
  filter?: Record<string, any>;
  readBehavior?: ReadBehavior;
}

export class PersistedArray<T = any> extends Array<T> {
  static get [Symbol.species]() {
    return Array;
  }

  reading = false;
  afterRead: (() => void) | null = null;
  readonly entityName: string = '';

  private _filter: Record<string, any> = {};
  private _entityClass: EntityArrayClass;
  private _idProperty: string;
  private _readBehavior?: ReadBehavior;

  constructor(
    entityClass: EntityArrayClass,
    options?: PersistedArrayOptions,
    idProperty = 'id',
  ) {
    super();
    this._entityClass = entityClass;
    this._idProperty = idProperty;
    if (options?.filter) this._filter = options.filter;
    this._readBehavior = options?.readBehavior;
  }

  get readFilter(): Record<string, any> {
    return this._filter;
  }

  set readFilter(value: Record<string, any>) {
    this._filter = value;
  }

  async read(filter?: Record<string, any>): Promise<void> {
    const mergedFilter = { ...this._filter, ...filter };
    if (filter) this._filter = mergedFilter;
    const apiFilter = stripClientOptions(mergedFilter);
    this.reading = true;
    try {
      const items = this._readBehavior
        ? await this._readBehavior(apiFilter)
        : await this._entityClass.find(apiFilter);
      this.length = 0;
      // find() returns entity instances; a behavior endpoint returns raw JSON —
      // hydrate so items behave the same however they were read
      this.push(
        ...items.map((item: any) =>
          item instanceof (this._entityClass as any)
            ? item
            : new (this._entityClass as any)(item),
        ),
      );
    } finally {
      this.reading = false;
    }
    if (this.afterRead) this.afterRead();
  }

  async add(data?: Record<string, any>): Promise<T> {
    const result = await this._entityClass.create(data || {});
    this.push(result);
    return result;
  }

  async remove(indexOrItem: number | T): Promise<void> {
    let index: number;
    if (typeof indexOrItem === 'number') {
      index = indexOrItem;
    } else {
      index = this.findIndex(
        (item) =>
          (item as any)?.[this._idProperty] ===
          (indexOrItem as any)[this._idProperty],
      );
      if (index === -1) return;
    }

    const item = this[index];
    const id = (item as any)?.[this._idProperty];
    if (id) {
      await this._entityClass.deleteById(id);
    }
    this.splice(index, 1);
  }
}
